import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  DevicesStore,
  Gateway,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  ZigbeeTopologyDeviceData,
} from '@store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RemoteDeviceStore } from '../../../../@store/remote-device/remote-device.store';
import { GraphElementCoordinates, GraphLineCoordinates } from './zigbee-topology';
import { ZigbeeTopologyDeviceComponent } from './topology-device/zigbee-topology-device.component';
import { CustomTranslationsService } from '../../../../operational-data-common/translations';
import { TranslateService } from '@ngx-translate/core';
import { NO_VALUE } from '@pipes';

@Component({
  selector: 'app-zigbee-topology-modal',
  templateUrl: './zigbee-topology-modal.component.html',
  styleUrls: ['./zigbee-topology-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZigbeeTopologyModalComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly OFFSET_LEFT = 41;
  readonly OFFSET_TOP = 20;
  readonly OFFSET_BOTTOM = 90;
  readonly NO_VALUE = NO_VALUE;
  zigbeeTopologyDeviceData: ZigbeeTopologyDeviceData;

  @ViewChild('container', { read: ElementRef })
  container: ElementRef;

  @ViewChild('svg', { read: ElementRef })
  svg: ElementRef;

  @ViewChildren(ZigbeeTopologyDeviceComponent, { read: ElementRef })
  devices: QueryList<ElementRef>;

  private graphElementsCoordinates: GraphElementCoordinates[] = [];
  private graphLinesCoordinates: GraphLineCoordinates[] = [];
  private line: SVGLineElement;
  private lines: SVGLineElement[] = [];
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  mainDeviceBusinessName: string;
  gatewaySerialNumber: string;

  constructor(
    public remoteDeviceStore: RemoteDeviceStore,
    public dialogRef: MatDialogRef<ZigbeeTopologyModalComponent>,
    private customTranslationsService: CustomTranslationsService,
    private devicesStore: DevicesStore,
    private installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService
  ) {
    this.line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  }

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.generateGraph();
  }

  ngOnInit(): void {
    this.remoteDeviceStore.zigbeeTopologyDeviceData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        this.zigbeeTopologyDeviceData = data;
      });

    this.devicesStore
      .getDeviceBusinessName()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((name: string) => {
        this.mainDeviceBusinessName = name;
      });

    this.installationDetailsStore.getSelectedGateway$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gateway: Gateway) => {
        this.gatewaySerialNumber = gateway.serial;
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.generateGraph();
    }, 300);
  }

  getDeviceNameByDeviceType(deviceType?: string): string {
    if (deviceType) {
      return this.translateService.instant(
        this.customTranslationsService.REMOTE_DEVICE_TYPES[deviceType] ?? deviceType
      );
    } else NO_VALUE;
  }

  getDeviceRoomName(deviceId: string): string {
    let name;
    this.installationFeaturesStore.getDeviceRoom(deviceId).subscribe((roomFeature) => {
      name = roomFeature?.properties?.name?.value;
    });

    return name;
  }

  generateGraph(): void {
    this.clearGraph();
    this.constructGraphElementsCoordinates();
    this.constructGraphLinesCoordinates();
    this.drawGraph();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private clearGraph(): void {
    this.graphLinesCoordinates = [];

    this.lines.forEach((line) => {
      this.svg.nativeElement.removeChild(line);
    });
    this.lines = [];
  }

  private constructGraphElementsCoordinates(): void {
    const containerRect = this.container.nativeElement.getBoundingClientRect();

    const coordinates = [];

    this.devices.forEach((entry, index) => {
      if (index === 0) {
        return;
      }

      const rect = entry.nativeElement.getBoundingClientRect();
      const parentRect = entry.nativeElement.parentNode.getBoundingClientRect();

      coordinates.push({
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top,
        parentOffsetX: parentRect.x - containerRect.left,
        parentOffsetY: parentRect.y - containerRect.top,
      });
    });

    this.graphElementsCoordinates = coordinates;
  }

  private constructGraphLinesCoordinates(): void {
    //short connector arrow
    this.graphElementsCoordinates.forEach((entry) => {
      this.graphLinesCoordinates.push({
        x1: entry.x + this.OFFSET_LEFT,
        y1: entry.y,
        x2: entry.x + this.OFFSET_LEFT,
        y2: entry.y + this.OFFSET_TOP,
        isMarker: true,
      });
    });

    //horizontal line
    this.graphElementsCoordinates.forEach((entry) => {
      this.graphLinesCoordinates.push({
        x1: entry.parentOffsetX + this.OFFSET_LEFT,
        y1: entry.y,
        x2: entry.x + this.OFFSET_LEFT,
        y2: entry.y,
        isMarker: false,
      });
    });

    //main vertical line
    this.graphElementsCoordinates.forEach((entry) => {
      this.graphLinesCoordinates.push({
        x1: entry.parentOffsetX + this.OFFSET_LEFT,
        y1: entry.parentOffsetY - this.OFFSET_BOTTOM,
        x2: entry.parentOffsetX + this.OFFSET_LEFT,
        y2: entry.y,
        isMarker: false,
      });
    });
  }

  private drawGraph(): void {
    this.graphLinesCoordinates.forEach((coordinates) => {
      const newLine = (<unknown>this.line.cloneNode(false)) as SVGLineElement;

      newLine.setAttribute('x1', `${coordinates.x1}`);
      newLine.setAttribute('y1', `${coordinates.y1}`);
      newLine.setAttribute('x2', `${coordinates.x2}`);
      newLine.setAttribute('y2', `${coordinates.y2}`);

      if (coordinates.isMarker) {
        newLine.setAttribute('marker-end', `url(#arrow)`);
      }

      this.lines.push(newLine);

      this.svg.nativeElement.appendChild(newLine);
    });
  }
}
